import { useMutation } from "@tanstack/react-query"
import { Form, Input, Button, message, Space, Row, Col } from "antd"
import { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  AuthActions,
  AuthContext,
  AuthForms,
} from "../../contexts/auth/AuthContext"
import { LOGIN, REGISTER_TENANT } from "./mutations"

const SignInForm = () => {
  const { t } = useTranslation()
  const [isRegistering, setIsRegistering] = useState(false)
  const { authState, authDispatch } = useContext(AuthContext)
  const {
    mutate: login,
    isLoading: loading,
    error,
    data,
  } = useMutation(LOGIN, {
    onSuccess: (data: any) => {
      if (data?.accessToken) {
        const photoUrl = data?.user?.photoUrl || data?.user?.PhotoUrl || ""
        const photoFileName = data?.user?.photoFileName || data?.user?.PhotoFileName || ""
        if (photoUrl) {
          localStorage.setItem("user_profile_photo", photoUrl)
          if (photoFileName) {
            localStorage.setItem("user_profile_photo_filename", photoFileName)
          }
          window.dispatchEvent(new Event("profile_photo_updated"))
        }
        authDispatch({
          type: AuthActions.SIGN_IN,
          payload: {
            user: data?.user,
            accessToken: data?.accessToken,
            refreshToken: data?.refreshToken,
          },
        })
        message.success(t("welcomeBack"))
      }
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || t("invalidCredentials") || "Login yoki parol noto'g'ri"
      message.error(msg)
    },
  })
  const {
    mutate: register,
    isLoading: rLoading,
    error: rError,
  } = useMutation(REGISTER_TENANT, {
    onSuccess: (data: any) => {
      message.success(data?.message || t("tenantRegistered"))
      setIsRegistering(false)
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || t("error"))
    },
  })

  async function onFinish(values: any) {
    if (!values) return

    if (isRegistering) {
      try {
        await register({
          companyName: values.companyName,
          email: values.email,
          password: values.password,
          confirmPassword: values.confirmPassword,
          phone: values.phone,
          activationCode: values.activationCode,
        })
      } catch (error: any) {
        message.error(error.toString())
      }
      return
    }

    if (authState.currentForm === AuthForms.FORGOT_PASSWORD) {
      message.success(t("newPasswordSend"))
      authDispatch({ type: AuthActions.TOGGLE_FORM })
      return
    }

    if (!isRegistering) {
      try {
        await login({
          email: values.email,
          password: values.password,
        })
      } catch (err: any) {
        message.error(err.toString())
      }
    }
  }

  return (
    <Form
      name="sign-in-form"
      className="login-form"
      initialValues={{ remember: true }}
      layout="vertical"
      style={{ width: "100%" }}
      onFinish={onFinish}
    >
      {!isRegistering ? (
        <>
          <Form.Item
            name="email"
            label={t("loginOrEmail", String(t("loginOrEmail")))}
            rules={[{ required: true, message: t("loginOrEmailPlaceholder", String(t("loginOrEmailPlaceholder"))) }]}
          >
            <Input
              size="large"
              autoComplete="username"
              type="text"
              placeholder="admin@gmail.com"
            />
          </Form.Item>

          {authState.currentForm !== AuthForms.FORGOT_PASSWORD && (
            <Form.Item
              name="password"
              label={t("password")}
              rules={[{ required: true, message: t("passwordPlaceholder") }]}
            >
              <Input.Password
                size="large"
                autoComplete="currentPassword"
                type="password"
                placeholder={t("passwordPlaceholder")}
              />
            </Form.Item>
          )}
        </>
      ) : (
        <Row gutter={16}>
          <Col xs={24}>
            <div className="signup-note">{t("tenantSignupHint")}</div>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="companyName"
              label={t("companyName")}
              rules={[{ required: true, message: String(t("required")) }]}
            >
              <Input size="large" placeholder="Alfa Savdo MChJ" />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="email"
              label={t("adminEmail")}
              rules={[
                { required: true, message: String(t("required")) },
                { type: "email", message: String(t("invalidEmail")) },
              ]}
              extra={t("adminEmailHint")}
            >
              <Input size="large" placeholder="admin@alfa.uz" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="password"
              label={t("password")}
              rules={[
                { required: true, message: String(t("required")) },
                { min: 8, message: String(t("passwordTooShort")) },
              ]}
            >
              <Input.Password size="large" placeholder={t("password")} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="confirmPassword"
              label={t("confirmPassword")}
              dependencies={["password"]}
              rules={[
                { required: true, message: String(t("required")) },
                ({ getFieldValue }: any) => ({
                  validator(_: any, value: string) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error(String(t("passwordMismatch"))))
                  },
                }),
              ]}
            >
              <Input.Password size="large" placeholder={t("confirmPassword")} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item name="phone" label={t("phone")}>
              <Input size="large" placeholder="+998 90 123 45 67" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            {/*
              Taklif kaliti. Tizim egasi uni talab qilmasa maydon bo'sh
              qoldiriladi; talab qilinsa — busiz o'tib bo'lmaydi va
              backend buni ochiq aytadi.
            */}
            <Form.Item
              name="activationCode"
              label={t("inviteKey")}
              extra={t("inviteKeyFieldHint")}
            >
              <Input size="large" placeholder="ZEN-XXXX-XXXX-XXXX" />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Space
        style={{
          justifyContent: "space-between",
          width: "100%",
          marginBottom: 24,
        }}
      >
        <Button
          style={{ paddingLeft: 0 }}
          type="link"
          onClick={() => authDispatch({ type: AuthActions.TOGGLE_FORM })}
          className="login-form-forgot"
        >
          {authState.currentForm === AuthForms.FORGOT_PASSWORD
            ? t("rememberedPassword")
            : t("forgotPassword")}
        </Button>
        <Button
          style={{ paddingLeft: 0 }}
          type="link"
          onClick={() => setIsRegistering(!isRegistering)}
          className="login-form-forgot"
        >
          {isRegistering ? t("signIn") : t("register")}
        </Button>
      </Space>

      <Button
        block
        type="primary"
        htmlType="submit"
        loading={loading || rLoading}
        danger={!!error || !!rError}
        className="login-form-button"
      >
        {authState.currentForm === AuthForms.FORGOT_PASSWORD
          ? t("send")
          : !isRegistering
          ? t("signIn")
          : t("register")}
      </Button>
    </Form>
  )
}

export default SignInForm
